import { useMemo } from "react";

import { useClientStatusLabel, useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";

import { SystemDropdownCategory, UpdateClientStatusCounselorRequest, clientsApi } from "@/api";
import { useCounselors } from "@/common/components/counselor-select/hooks/useCounselors";
import { useSystemDropdownOptions } from "@/common/hooks/useSystemDropdownOptions";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export interface StatusCounselorFormType {
  status: string | null;
  assignedCounselorId: string | null;
}

interface InitialValues {
  status: string | null;
  assignedCounselorId: string | null;
}

const validationSchema = Yup.object().shape({
  status: Yup.string().nullable(),
  assignedCounselorId: Yup.string().nullable(),
});

export function useStatusCounselor(clientId: string, defaults: InitialValues) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();

  const { data: statusCounselorData } = useQuery({
    queryKey: [QueryKey.ClientStatusCounselor, clientId],
    queryFn: () => clientsApi.getClientStatusCounselor({ clientId }),
  });

  const { optionItems: statusOptions } = useSystemDropdownOptions(SystemDropdownCategory.ClientStatus);
  const statusLabelOf = useClientStatusLabel();

  const { counselorOptions } = useCounselors();

  const updateMutation = useMutation({
    mutationFn: (request: UpdateClientStatusCounselorRequest) =>
      clientsApi.updateClientStatusCounselor({
        clientId,
        updateClientStatusCounselorRequest: request,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData([QueryKey.ClientStatusCounselor, clientId], saved);
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Client, clientId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Clients] });
      showSuccess(t("clients.client.masterData.statusCounselorSection.successfullySaved"));
    },
    onError: (error) => {
      showError(error.message);
    },
  });

  const statusHistory = statusCounselorData?.statusHistory ?? [];
  const currentStatus = statusHistory[statusHistory.length - 1]?.status ?? null;

  const initialValues: StatusCounselorFormType = useMemo(() => {
    return {
      status: currentStatus ?? defaults.status,
      assignedCounselorId: defaults.assignedCounselorId,
    };
  }, [currentStatus, defaults.status, defaults.assignedCounselorId]);

  const formik = useFormikWithDirtyForm<StatusCounselorFormType>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      await updateMutation.mutateAsync({
        status: values.status,
        assignedCounselorId: values.assignedCounselorId,
      });
    },
  });

  return {
    formik,
    statusHistory,
    statusOptions,
    statusLabelOf,
    counselorOptions,
  };
}
